import React, { FC, ReactNode, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ExperienceApi } from 'src/api';
import { useUserContext } from 'src/context/user';

export type ExperienceType = 'classic' | '2024' | '2025';

export enum ExperienceTypes {
  Classic = 'classic',
  _2024 = '2024',
  _2025 = '2025',
}

export type ExperienceContextValue = {
  isLoaded: boolean;
  isError: boolean;
  experienceType: ExperienceType | null;
};

const defaultValue: ExperienceContextValue = {
  isLoaded: false,
  isError: false,
  experienceType: null,
};

export const ExperienceContext = React.createContext<{
  isLoaded: boolean;
  isError: boolean;
  experienceType: ExperienceType | null;
}>(defaultValue);

export const useExperienceContext = () => useContext(ExperienceContext);

export const ExperienceProvider: FC<{ children?: ReactNode }> = ({ children }) => {
  const { user } = useUserContext();

  const { data: experienceData, isError } = useQuery({
    queryKey: ['experience', user?.id],
    queryFn: () => ExperienceApi.getExperience(),
    enabled: !!user?.id,
  });

  const experienceType = experienceData?.current || null;

  const experience = useMemo(
    () => ({
      isLoaded: experienceType !== null,
      isError,
      experienceType,
    }),
    [experienceType, isError],
  );

  return <ExperienceContext.Provider value={experience}>{children}</ExperienceContext.Provider>;
};
